import React, { SetStateAction, useEffect, useState } from 'react';
import { Link } from '@reach/router';
import { Dispatch } from 'react';
import CrossIcon from '../../images/Icon_Cross_01.png';
import MinusIcon from '../../images/Icon_Minus_01.png';
import ToggleSwitch from "./ToggleSwitch";
import Cookies from 'universal-cookie';
import { DOMAIN } from '../../endpoints.config';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';

type CookieSettingsPopupProps = {
  toggleCookieSettings: boolean;
  setToggleCookieSettings: Dispatch<SetStateAction<boolean>>;
};

type CookiesListDescProps = {
  category: string;
  open: boolean;
  name: string;
  description: string;
};

type CookiesListProps = {
  category: string;
  name: string;
};

const CookieSettingsPopup: React.FC<CookieSettingsPopupProps> = ({
  toggleCookieSettings,
  setToggleCookieSettings
}) => {
  const [cookiesList, setCookiesList] = useState<CookiesListDescProps[]>(getCookieDescriptions());
  const [cookiesListUpdated, setCookiesListUpdated] = useState<boolean>(false);
  const [functionalCookiesOn, setFunctionalCookiesOn] = useState<boolean>(true);
  const [marketingCookiesOn, setMarketingCookiesOn] = useState<boolean>(true);


  const acceptSiteCookies = () => {
    const cookies = new Cookies();
    const date = new Date();
    date.setMonth(date.getMonth() + 1);

    cookies.set('cookie_settings', "all_cookies", {
      expires: date,
      domain: DOMAIN,
      sameSite: "none",
      secure: true
    }); 

    window.location.reload();
  };

  const rejectSelectedSiteCookies = () => {
    let cookieValStr = "";
    
    if (!functionalCookiesOn && !marketingCookiesOn) {
      cookieValStr = "functional_and_marketing";
    } else if (!functionalCookiesOn) {
      cookieValStr = "functional";
    } else if (!marketingCookiesOn) {
      cookieValStr = "marketing";
    }                        

    const cookies = new Cookies();
    const date = new Date();
    date.setMonth(date.getMonth() + 1);

    cookies.set('cookie_settings', cookieValStr, {
      expires: date,
      domain: DOMAIN,
      sameSite: "none",
      secure: true
    });

    window.location.reload();
  };

  const rejectSiteCookies = () => {
    const cookies = new Cookies();
    const date = new Date();
    date.setMonth(date.getMonth() + 1);

    cookies.set('cookie_settings', "functional_and_marketing", {
      expires: date,
      domain: DOMAIN,
      sameSite: "none",
      secure: true
    });

    window.location.reload();
  };  


  useEffect(() => {
    if(cookiesListUpdated){
      setCookiesListUpdated(false);
    }
  }, [cookiesListUpdated]);

  const toggleCookieShow = (cookie_index: number) => {
    let cookiesListCopy = cookiesList;
    cookiesListCopy[cookie_index].open = !cookiesListCopy[cookie_index].open;
    setCookiesList(cookiesListCopy);
    setCookiesListUpdated(true);
  };


  const submitSelectedCookies = () => {
    if(functionalCookiesOn && marketingCookiesOn){
      acceptSiteCookies();
    } else {
      rejectSelectedSiteCookies();
    }

    window.location.reload();
  };


  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={toggleCookieSettings}
      hdrTitle={'Cookie settings'}
      position="right"
      primaryBtnTxt="Accept selected"
      givenClass="CookieSettingsPopup"
      primaryBtnColor="BtnRed"
      handleClose={() => setToggleCookieSettings(false)}
      handlePrimaryBtn={() => submitSelectedCookies()}
      width={460}
    >
      <>
        <p>
          We use cookies to give you the best online experience. By using our
          website you agree to our use of cookies in accordance with our cookie
          policy. <a href="https://getset4education.co.uk/info/cookie-policy" target="_blank" rel="noreferrer">Learn more here</a>
        </p>

        <div className="AcceptCookieBtnRow">
          <div className="Btn AcceptBtn" onClick={() => { acceptSiteCookies(); setToggleCookieSettings(false); }}>Accept all</div>
          <div className="Btn RejectBtn" onClick={() => { rejectSiteCookies(); setToggleCookieSettings(false); }}>Reject All Non-Essential</div>
        </div>

        <div className="CookieCategoryList">
          <div className="CategoryBlock">
            <div className="CBInner">
              <div className="BtnCol">
                <ToggleSwitch
                  onChange={(e) => { }}
                  value={true}
                  disable={true}
                />
              </div>
              <div className="InfoCol">
                <div className="CategoryHdr">Necessary cookies</div>
                <p>Cookies that are used by the website to function properly, without which the site would not work.</p>

                <div className="CookiesList">
                  {cookiesList.map((item, index) => {
                    if(item.category === "necessary"){
                      return (
                        <div className="CookieDetailsRow" key={"Necessary_Cookie_Detail_Row_" + index}>
                          <div className="CDInner">
                            <div 
                              className="ToggleIconCol"
                              onClick={() => toggleCookieShow(index)}
                            >
                              <img src={item.open ? MinusIcon : CrossIcon} alt={(item.open ? "Close" : "Open") + " cookie " + item.name} />
                            </div>
                            <div className="InfoTxtCol">
                              <div className="CookieLabel">{item.name}</div>
                              {item.open &&
                                <div className="CookieDesc">
                                  <p>{item.description}</p>
                                </div>
                              }
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="CategoryBlock">
            <div className="CBInner">
              <div className="BtnCol">
                <ToggleSwitch
                  onChange={(e) => setFunctionalCookiesOn(!functionalCookiesOn)}
                  value={functionalCookiesOn}
                />
              </div>
              <div className="InfoCol">
                <div className="CategoryHdr">Functional cookies</div>
                <p>Cookies that help enhance a website's performance and functionality.</p>

                <div className="CookiesList">
                  {cookiesList.map((item, index) => {
                    if(item.category === "functional"){
                      return (
                        <div className="CookieDetailsRow" key={"Functional_Cookie_Detail_Row_" + index}>
                          <div className="CDInner">
                            <div 
                              className="ToggleIconCol"
                              onClick={() => toggleCookieShow(index)}
                            >
                              <img src={item.open ? MinusIcon : CrossIcon} alt={(item.open ? "Close" : "Open") + " cookie " + item.name} />
                            </div>
                            <div className="InfoTxtCol">
                              <div className="CookieLabel">{item.name}</div>
                              {item.open &&
                                <div className="CookieDesc">
                                  <p>{item.description}</p>
                                </div>
                              }
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="CategoryBlock">
            <div className="CBInner">
              <div className="BtnCol">
                <ToggleSwitch
                  onChange={(e) => setMarketingCookiesOn(!marketingCookiesOn)}
                  value={marketingCookiesOn}
                />
              </div>
              <div className="InfoCol">
                <div className="CategoryHdr">Marketing cookies</div>
                <p>Cookies used to track users' online behavior for marketing purposes.</p>

                <div className="CookiesList">
                  {cookiesList.map((item, index) => {
                    if(item.category === "marketing"){
                      return (
                        <div className="CookieDetailsRow" key={"Marketing_Cookie_Detail_Row_" + index}>
                          <div className="CDInner">
                            <div 
                              className="ToggleIconCol"
                              onClick={() => toggleCookieShow(index)}
                            >
                              <img src={item.open ? MinusIcon : CrossIcon} alt={(item.open ? "Close" : "Open") + " cookie " + item.name} />
                            </div>
                            <div className="InfoTxtCol">
                              <div className="CookieLabel">{item.name}</div>
                              {item.open &&
                                <div className="CookieDesc">
                                  <p>{item.description}</p>
                                </div>
                              }
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })}
                </div>
              </div>
            </div>
          </div>                        
        </div>
      </>
    </PopupOrPanel>
  );
};


const getCookieDescriptions = () => {
  return [
    { category: "necessary", open: false, name: "AT", description: "Holds access token allowing access to user session." },
    { category: "necessary", open: false, name: "ATE", description: "Holds access token expiry date for when the user session ends." },
    { category: "necessary", open: false, name: "RT", description: "Holds token to refresh the access token to keep existing user session." },
    { category: "necessary", open: false, name: "RTE", description: "Holds refresh token expiry date." },
    { category: "necessary", open: false, name: "lastExternalReferrer", description: "Used to store the last external referrer for a user. It is set by the Google Analytics JavaScript library. This cookie is created after a user initially clicks a client's ad, and is used to track user clicks and interactions on the website with a user ID." },
    { category: "necessary", open: false, name: "lastExternalReferrerTime", description: "Detects how the user reached the website by registering their last URL-address." },
    { category: "necessary", open: false, name: "__RequestVerificationToken_Lw__", description: "An anti-forgery cookie set by web applications built using ASP.NET MVC technologies. It is designed to stop unauthorized posting of content to the website, known as Cross-Site Request Forgery." },
    { category: "necessary", open: false, name: "ASP.NET_SessionId", description: "An asp.net cookie that is automatically generated to identify the users session on the server." },
    { category: "necessary", open: false, name: "GETSET_SchoolUser / SchoolUser", description: "Holds the login session detail for the user, storing the info of who is logged in and what they have access to." },
    { category: "necessary", open: false, name: "__stripe_mid / __stripe_sid", description: "To remember who a user is and to enable the website to process payments without storing any credit card information on its servers." },
    { category: "functional", open: false, name: "UI", description: "Provides user initials for display on logged in user menu." },
    { category: "functional", open: false, name: "Accessibility mode", description: "Tailors the site for accessible users." },    
    { category: "marketing", open: false, name: "_clck", description: "Installed by Microsoft Clarity to store information of how visitors use a website and help in creating an analytics report of how the website is doing. The data collected includes the number of visitors, the source where they have come from, and the pages visited in an anonymous form." },
    { category: "marketing", open: false, name: "_clsk", description: "A first-party cookie developed and installed by Microsoft Clarity. Its primary job is to store and collect pageviews to compile into a single Clarity session recording, creating valuable insights for the site owner." },
    { category: "marketing", open: false, name: "_fbp", description: "A Facebook cookie used to distinguish and keep track of unique users. When the Facebook pixel is installed on a website, and the pixel uses first-party cookies, the pixel automatically saves a unique identifier to an _fbp cookie for the website domain if one does not already exist." },
    { category: "marketing", open: false, name: "_ga", description: "This cookie is used by Google Analytics to distinguish between users. It calculates visitor, session, campaign data and keeps track of site usage for the site's analytics report. It stores information anonymously and assigns a randomly generated number to identify unique visitors." },
    { category: "marketing", open: false, name: "_gcl_au", description: "Used by Google AdSense for experimenting with advertisement efficiency across websites using their services." },
    { category: "marketing", open: false, name: "_gid", description: "This cookie is used by Google Analytics for user identification. It stores information on how visitors use a website and helps in creating an analytics report of how the website is doing. The data collected includes the number of visitors, the source where they have come from, and the pages visited in an anonymous form." }
  ];
};


export default CookieSettingsPopup;
